import { useState, useEffect, useRef } from 'react'

interface Dimensions {
  width: number
  height: number
}

function useElementDimensions<T extends HTMLElement>(
  ref: React.RefObject<T>
): Dimensions | null {
  const [dimensions, setDimensions] = useState<Dimensions | null>(null)
  const observer = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    if (!ref.current) {
      return undefined
    }

    const updateDimensions = () => {
      if (ref.current) {
        const { offsetWidth: width, offsetHeight: height } = ref.current
        setDimensions({ width, height })
      }
    }

    // Initialize dimensions
    updateDimensions()

    // Observe size changes using ResizeObserver
    observer.current = new ResizeObserver(() => {
      requestAnimationFrame(updateDimensions)
    })

    const currentObserver = observer.current

    currentObserver.observe(ref.current)

    // Clean up
    return () => {
      if (currentObserver) {
        currentObserver.disconnect()
      }
    }
  }, [ref, ref.current]) // eslint-disable-line

  return dimensions
}

export default useElementDimensions
